import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { InputTextarea } from "primereact/inputtextarea";
import { RadioButton } from "primereact/radiobutton";
import { Toast } from "primereact/toast";
import React, { useRef, useState } from "react";
import { Controller, Path, useForm } from "react-hook-form";
import {
    appInnerCardStyle,
    appSectionCardStyle,
    renderSurfaceHeader,
} from "../../../../components/general/uiSurfaceStyles";
import { useNurseFormAutoSave } from "../useNurseFormAutoSave";

type YNA = "Y" | "N" | "unableToAssess" | "";

export interface Step07FormValues {
    // Delirium
    deliriumSuddenOnset: boolean;
    deliriumDisorganizedSpeech: boolean;
    // Depression screening
    deprDepressedMood: YNA;
    deprChangeInAppetite: YNA;
    deprWeightChange: YNA;
    deprInsomnia: YNA;
    deprPsychomotorAgitation: YNA;
    deprDecreasedEnergy: YNA;
    deprWorthlessnessGuilt: YNA;
    deprDifficultyThinking: YNA;
    deprSuicidalThoughts: YNA;
    // Depression history
    deprHistHospitalization: boolean;
    deprFollowupHospitalization: boolean;
    deprHistPriorMedication: boolean;
    deprFollowupPriorMedication: boolean;
    deprHistPriorTreatments: boolean;
    deprFollowupPriorTreatments: boolean;
    deprComments: string;
    // Anxiety history
    anxHistHospitalization: boolean;
    anxFollowupHospitalization: boolean;
    anxHistPriorMedication: boolean;
    anxFollowupPriorMedication: boolean;
    anxHistPriorTreatments: boolean;
    anxFollowupPriorTreatments: boolean;
    anxComments: string;
    // General
    mentalHealthComments: string;
}

export const DEFAULT_STEP07: Step07FormValues = {
    deliriumSuddenOnset: false, deliriumDisorganizedSpeech: false,
    deprDepressedMood: "", deprChangeInAppetite: "", deprWeightChange: "", deprInsomnia: "",
    deprPsychomotorAgitation: "", deprDecreasedEnergy: "", deprWorthlessnessGuilt: "",
    deprDifficultyThinking: "", deprSuicidalThoughts: "",
    deprHistHospitalization: false, deprFollowupHospitalization: false,
    deprHistPriorMedication: false, deprFollowupPriorMedication: false,
    deprHistPriorTreatments: false, deprFollowupPriorTreatments: false,
    deprComments: "",
    anxHistHospitalization: false, anxFollowupHospitalization: false,
    anxHistPriorMedication: false, anxFollowupPriorMedication: false,
    anxHistPriorTreatments: false, anxFollowupPriorTreatments: false,
    anxComments: "",
    mentalHealthComments: "",
};

type DepressionKey =
    | "deprDepressedMood" | "deprChangeInAppetite" | "deprWeightChange" | "deprInsomnia"
    | "deprPsychomotorAgitation" | "deprDecreasedEnergy" | "deprWorthlessnessGuilt"
    | "deprDifficultyThinking" | "deprSuicidalThoughts";

const DEPRESSION_ITEMS: Array<{ key: DepressionKey; label: string }> = [
    { key: "deprDepressedMood", label: "Depressed mood, irritable mood, or loss of interest or pleasure in nearly all activities" },
    { key: "deprChangeInAppetite", label: "Change in appetite" },
    { key: "deprWeightChange", label: "Weight gain or loss (>5% of body weight)" },
    { key: "deprInsomnia", label: "Insomnia or hyper-somnia (sleeping all the time)" },
    { key: "deprPsychomotorAgitation", label: "Psychomotor agitation or retardation (slowed speech, thinking, or body movements)" },
    { key: "deprDecreasedEnergy", label: "Decreased energy and fatigue without physical exertion" },
    { key: "deprWorthlessnessGuilt", label: "Feelings of worthlessness or guilt" },
    { key: "deprDifficultyThinking", label: "Difficulty thinking, concentrating, or making decisions" },
    { key: "deprSuicidalThoughts", label: "Recurrent thoughts of death, suicide ideation (does client have a plan or attempt?)" },
];

export interface Step07Props {
    sectionData: Partial<Step07FormValues> | null;
    residentId: string;
    formId: string;
    onSave: (data: Step07FormValues) => Promise<void>;
    onNext: () => void;
    onBack: () => void;
    loading?: boolean;
}

const InfoRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
    <div className="col-12 md:col-4 mb-2">
        <div className="text-xs font-medium mb-1" style={{ color: "#6b7280" }}>{label}</div>
        <div className="text-sm font-medium" style={{ color: value ? "#111827" : "#9ca3af" }}>{value || "—"}</div>
    </div>
);

const formatYnaValue = (value: YNA): string => {
    if (value === "Y") return "Yes";
    if (value === "N") return "No";
    if (value === "unableToAssess") return "Unable to assess";
    return "—";
};

const Step07MentalHealthScreening: React.FC<Step07Props> = ({
    sectionData, formId, onSave, onNext, onBack, loading,
}) => {
    const toast = useRef<Toast>(null);
    const [enableEditing, setEnableEditing] = useState<boolean>(false);
    const defaultValues: Step07FormValues = { ...DEFAULT_STEP07, ...(sectionData ?? {}) };
    const { control, handleSubmit, watch, reset } = useForm<Step07FormValues>({ defaultValues });

    const onSubmit = async (data: Step07FormValues) => { await onSave(data); };
    const handleSave = async (data: Step07FormValues) => {
        try {
            await onSave(data);
            toast.current?.show({ severity: "success", summary: "Saved", detail: "Mental health screening saved.", life: 3000 });
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: "Save failed", detail: error?.message, life: 4000 });
        }
    };
    const handleSaveAndContinue = async (data: Step07FormValues) => {
        try {
            await onSave(data);
            toast.current?.show({ severity: "success", summary: "Saved", detail: "Saved. Moving to next step.", life: 2000 });
            onNext();
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: "Save failed", detail: error?.message, life: 4000 });
        }
    };
    useNurseFormAutoSave({ enabled: enableEditing && !!formId, baselineData: defaultValues, watch, handleSubmit, onSubmit });

    const values = watch();

    const cb = (name: Path<Step07FormValues>, label: string) => (
        <Controller key={name} name={name} control={control} render={({ field: f }) => (
            <div className="flex align-items-center gap-2 mb-2">
                <Checkbox inputId={name} checked={!!f.value} onChange={e => f.onChange(e.checked)} />
                <label htmlFor={name} className="text-sm cursor-pointer">{label}</label>
            </div>
        )} />
    );

    const historyRow = (histKey: Path<Step07FormValues>, followupKey: Path<Step07FormValues>, label: string) => (
        <div key={label} style={appInnerCardStyle} className="mb-2 p-2">
            <div className="flex align-items-center justify-content-between">
                <span className="text-sm flex-1">{label}</span>
                <div className="flex gap-3 ml-3">
                    <Controller name={histKey} control={control} render={({ field: f }) => (
                        <div className="flex align-items-center gap-1">
                            <Checkbox inputId={`${histKey}_h`} checked={!!f.value} onChange={e => f.onChange(e.checked)} />
                            <label htmlFor={`${histKey}_h`} className="text-xs">History</label>
                        </div>
                    )} />
                    <Controller name={followupKey} control={control} render={({ field: f }) => (
                        <div className="flex align-items-center gap-1">
                            <Checkbox inputId={`${followupKey}_f`} checked={!!f.value} onChange={e => f.onChange(e.checked)} />
                            <label htmlFor={`${followupKey}_f`} className="text-xs">Need Follow-up</label>
                        </div>
                    )} />
                </div>
            </div>
        </div>
    );

    return (
        <>
            <Toast ref={toast} />
            <form onSubmit={handleSubmit(handleSave)}>
                <div className="flex justify-content-center gap-2 mb-3">
                    <Button type="button" label="Prev" icon="pi pi-arrow-left" severity="secondary" outlined rounded onClick={onBack} />
                    <Button type="button" label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded visible={!enableEditing} onClick={() => setEnableEditing(true)} />
                    <Button type="button" label="Disable Edit" icon="pi pi-lock" severity="secondary" outlined rounded visible={enableEditing} onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
                    <Button type="submit" label="Save" icon="pi pi-save" severity="warning" outlined rounded loading={loading} visible={enableEditing} />
                    <Button type="button" label="Next" icon="pi pi-arrow-right" iconPos="right" severity="info" outlined rounded onClick={onNext} />
                </div>
                {!enableEditing ? (
                    <>
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Delirium Screening", "Delirium indicators and sudden changes in functioning.", "pi pi-exclamation-circle")}
                            <div className="p-3 grid">
                                <InfoRow label="Sudden or new onset / change in mental functioning" value={values.deliriumSuddenOnset ? "Yes" : "No"} />
                                <InfoRow label="Episode of disorganized speech" value={values.deliriumDisorganizedSpeech ? "Yes" : "No"} />
                            </div>
                        </Card>

                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Depression Screening", "All saved depression indicators and relevant history.", "pi pi-heart")}
                            <div className="p-3 grid">
                                {DEPRESSION_ITEMS.map((item) => (
                                    <InfoRow key={item.key} label={item.label} value={formatYnaValue(values[item.key])} />
                                ))}
                                <InfoRow
                                    label="Hospitalization"
                                    value={`History: ${values.deprHistHospitalization ? "Yes" : "No"} • Follow-up: ${values.deprFollowupHospitalization ? "Yes" : "No"}`}
                                />
                                <InfoRow
                                    label="Prior Medication"
                                    value={`History: ${values.deprHistPriorMedication ? "Yes" : "No"} • Follow-up: ${values.deprFollowupPriorMedication ? "Yes" : "No"}`}
                                />
                                <InfoRow
                                    label="Prior Treatments"
                                    value={`History: ${values.deprHistPriorTreatments ? "Yes" : "No"} • Follow-up: ${values.deprFollowupPriorTreatments ? "Yes" : "No"}`}
                                />
                                <InfoRow label="Depression Comments" value={values.deprComments} />
                            </div>
                        </Card>

                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Relevant History of Anxiety", "Anxiety history and follow-up needs.", "pi pi-info-circle")}
                            <div className="p-3 grid">
                                <InfoRow
                                    label="Hospitalization"
                                    value={`History: ${values.anxHistHospitalization ? "Yes" : "No"} • Follow-up: ${values.anxFollowupHospitalization ? "Yes" : "No"}`}
                                />
                                <InfoRow
                                    label="Prior Medication"
                                    value={`History: ${values.anxHistPriorMedication ? "Yes" : "No"} • Follow-up: ${values.anxFollowupPriorMedication ? "Yes" : "No"}`}
                                />
                                <InfoRow
                                    label="Prior Treatments"
                                    value={`History: ${values.anxHistPriorTreatments ? "Yes" : "No"} • Follow-up: ${values.anxFollowupPriorTreatments ? "Yes" : "No"}`}
                                />
                                <InfoRow label="Anxiety Comments" value={values.anxComments} />
                                <InfoRow label="General Comments" value={values.mentalHealthComments} />
                            </div>
                        </Card>
                    </>
                ) : (
                    <>
                        {/* Delirium Screening */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Delirium Screening", "Check if any of the following represent a sudden change from the individual's regular functioning.", "pi pi-exclamation-circle")}
                            <div className="p-3">
                                <p className="text-sm text-color-secondary mb-3">
                                    Delirium can be due to a general medical condition (fall, infection, electrolyte imbalance) or substance-induced situation.
                                    If a box is checked, consider immediate referral to a medical health professional.
                                </p>
                                {cb("deliriumSuddenOnset", "Sudden or new onset/change in mental functioning (attention, awareness, coherence, or unpredictable variation over the course of the day)")}
                                {cb("deliriumDisorganizedSpeech", "Episode of disorganized speech (incoherent, nonsensical, irrelevant, or rambling; loses train of thought)")}
                            </div>
                        </Card>

                        {/* Depression Screening */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Depression Screening", "Individuals experiencing several of these signs for two weeks or more should seek professional advice.", "pi pi-heart")}
                            <div className="p-3">
                                {/* Header row */}
                                <div className="flex align-items-center mb-2 px-2" style={{ borderBottom: "2px solid #e5e7eb" }}>
                                    <span className="flex-1 font-semibold text-sm">Indicator</span>
                                    <div className="flex gap-4" style={{ minWidth: 220 }}>
                                        <span className="font-semibold text-sm w-6 text-center">Y</span>
                                        <span className="font-semibold text-sm w-6 text-center">N</span>
                                        <span className="font-semibold text-xs text-center" style={{ minWidth: 90 }}>Unable to assess</span>
                                    </div>
                                </div>
                                {DEPRESSION_ITEMS.map(item => (
                                    <Controller key={item.key} name={item.key as Path<Step07FormValues>} control={control} render={({ field: f }) => (
                                        <div className="flex align-items-center py-2 px-2" style={{ borderBottom: "1px solid #f3f4f6" }}>
                                            <span className="flex-1 text-sm">{item.label}</span>
                                            <div className="flex gap-4" style={{ minWidth: 220 }}>
                                                {(["Y", "N", "unableToAssess"] as const).map(val => (
                                                    <div key={val} className="flex justify-content-center" style={{ minWidth: val === "unableToAssess" ? 90 : 24 }}>
                                                        <RadioButton
                                                            inputId={`${item.key}_${val}`}
                                                            value={val}
                                                            checked={f.value === val}
                                                            onChange={() => f.onChange(val)}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )} />
                                ))}

                                {/* Depression history */}
                                <p className="font-semibold text-sm mt-4 mb-2">Relevant History of Depression</p>
                                {historyRow("deprHistHospitalization", "deprFollowupHospitalization", "Hospitalization")}
                                {historyRow("deprHistPriorMedication", "deprFollowupPriorMedication", "Prior Medication")}
                                {historyRow("deprHistPriorTreatments", "deprFollowupPriorTreatments", "Prior Treatments")}
                                <label className="font-medium text-sm block mt-2 mb-1">Comments (what has worked; what has not worked)</label>
                                <Controller name="deprComments" control={control} render={({ field: f }) => (
                                    <InputTextarea {...f} className="w-full" rows={2} autoResize placeholder="Depression comments" />
                                )} />
                            </div>
                        </Card>

                        {/* Anxiety History */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Relevant History of Anxiety", "Anxiety history and follow-up needs.", "pi pi-info-circle")}
                            <div className="p-3">
                                {historyRow("anxHistHospitalization", "anxFollowupHospitalization", "Hospitalization")}
                                {historyRow("anxHistPriorMedication", "anxFollowupPriorMedication", "Prior Medication")}
                                {historyRow("anxHistPriorTreatments", "anxFollowupPriorTreatments", "Prior Treatments")}
                                <label className="font-medium text-sm block mt-2 mb-1">Comments (what has worked; what has not worked)</label>
                                <Controller name="anxComments" control={control} render={({ field: f }) => (
                                    <InputTextarea {...f} className="w-full" rows={2} autoResize placeholder="Anxiety comments" />
                                )} />
                            </div>
                        </Card>

                        {/* General Comments */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("General Comments", "General comments on delirium, depression and anxiety screening.", "pi pi-comment")}
                            <div className="p-3">
                                <Controller name="mentalHealthComments" control={control} render={({ field: f }) => (
                                    <InputTextarea {...f} className="w-full" rows={4} autoResize placeholder="General comments on screening" />
                                )} />
                            </div>
                        </Card>

                    </>
                )}
                <div className="flex justify-content-center gap-2 mt-3">
                    <Button type="button" label="Prev" icon="pi pi-arrow-left" severity="secondary" outlined rounded onClick={onBack} />
                    <Button type="button" label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded visible={!enableEditing} onClick={() => setEnableEditing(true)} />
                    <Button type="button" label="Disable Edit" icon="pi pi-lock" severity="secondary" outlined rounded visible={enableEditing} onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
                    <Button type="submit" label="Save" icon="pi pi-save" severity="warning" outlined rounded loading={loading} visible={enableEditing} />
                    <Button type="button" label="Next" icon="pi pi-arrow-right" iconPos="right" severity="info" outlined rounded onClick={onNext} />
                </div>
            </form>
        </>
    );
};

export default Step07MentalHealthScreening;
